/* eslint-disable max-lines-per-function */
import Icon from "components/Icon"
import { useCallback } from "react"

import CarouselArrows from "./CarouselArrows"
import CarouselCounter from "./CarouselCounter"
import CarouselSlides from "./CarouselSlides"
import CarouselThumbnails from "./CarouselThumbnails"
import { DEFAULT_ZOOM_LABEL } from "./constants"
import {
  CarouselContainer,
  CarouselControls,
  CarouselShareButton,
  ZoomTriggerButton
} from "./styled"
import type { CarouselViewProps } from "./types"

const CarouselView = (props: CarouselViewProps) => {
  const {
    activeIndex,
    bounceDirection,
    children,
    className,
    columnGap,
    canGoNext,
    canGoPrev,
    counterPlacement,
    counterVariant,
    id,
    isShowArrows,
    isShowCounter,
    isShowCounterTop,
    isShowThumbnails,
    isOverlayLayout,
    name,
    onNext,
    onPrev,
    onSelectIndex,
    onThumbnailPress,
    onTouchEnd,
    onTouchMove,
    onTouchStart,
    slidesToShow,
    shareButtonProps,
    shareButtonPlacement,
    thumbnails,
    thumbnailsContainerRef,
    translateX,
    translateXOffset,
    zoomButtonHandler,
    zoomButtonLabel
  } = props

  const handleShareClick = useCallback(() => {
    if (shareButtonProps) {
      shareButtonProps.onPress()
    }
  }, [shareButtonProps])

  const hasControls = Boolean(shareButtonProps) || Boolean(zoomButtonHandler)

  const controls = hasControls ? (
    <CarouselControls $placement={shareButtonPlacement ?? "top-right"}>
      {shareButtonProps ? (
        <CarouselShareButton
          ariaLabel={shareButtonProps.ariaLabel}
          onClick={handleShareClick}
        />
      ) : null}

      {zoomButtonHandler ? (
        <ZoomTriggerButton
          aria-label={zoomButtonLabel ?? DEFAULT_ZOOM_LABEL}
          onClick={zoomButtonHandler}
          type="button"
        >
          <Icon group="fa6" name="FaUpRightAndDownLeftFromCenter" size="18px" />
        </ZoomTriggerButton>
      ) : null}
    </CarouselControls>
  ) : null

  const slidesNode = (
    <CarouselSlides
      columnGap={columnGap}
      id={id}
      name={name}
      slidesToShow={slidesToShow}
      translateX={translateX}
      translateXOffset={translateXOffset}
    >
      {children}
    </CarouselSlides>
  )

  const arrowsNode = (
    <CarouselArrows
      canGoNext={canGoNext}
      canGoPrev={canGoPrev}
      isShowArrows={isShowArrows}
      isShowThumbnails={isShowThumbnails}
      onNext={onNext}
      onPrev={onPrev}
    />
  )

  const counterNode = (
    <CarouselCounter
      activeIndex={activeIndex}
      childrenCount={children.length}
      counterPlacement={counterPlacement}
      counterVariant={counterVariant}
      isShowCounter={isShowCounter}
      isShowCounterTop={isShowCounterTop}
      isShowThumbnails={isShowThumbnails}
    />
  )

  const thumbnailsNode = (
    <CarouselThumbnails
      activeIndex={activeIndex}
      columnGap={columnGap}
      isShowThumbnails={isShowThumbnails}
      name={name}
      onSelectIndex={onSelectIndex}
      onThumbnailPress={onThumbnailPress}
      thumbnails={thumbnails}
      thumbnailsContainerRef={thumbnailsContainerRef}
    />
  )

  return (
    <CarouselContainer
      $bounceDirection={bounceDirection ?? null}
      $isOverlay={Boolean(isOverlayLayout)}
      aria-atomic="true"
      aria-label={name ? `${name} Carousel` : "Carousel"}
      aria-live="polite"
      className={className}
      onTouchEnd={onTouchEnd}
      onTouchMove={onTouchMove}
      onTouchStart={onTouchStart}
    >
      {controls}

      {slidesNode}

      {arrowsNode}

      {counterNode}

      {thumbnailsNode}
    </CarouselContainer>
  )
}

export default CarouselView
